export type DesktopWorkspace = { id: string; name: string; path: string };

export type DesktopWorkspacesApi = {
  list: () => Promise<DesktopWorkspace[]>;
  /** Opens the native OS folder picker on the desktop and grants the chosen folder(s). */
  addViaDialog: () => Promise<DesktopWorkspace[]>;
  remove: (id: string) => Promise<DesktopWorkspace[]>;
};

export const isDesktopApp = (): boolean => typeof window !== 'undefined' && 'desktopAPI' in window;

/**
 * The desktop app's workspace-management bridge (exposed via preload as
 * window.desktopAPI.workspaces). Returns null in the web app, so callers must
 * gate on it. Adding a folder triggers the native OS picker on the machine —
 * the secure, physical-presence path Claude/Codex use.
 */
export const getDesktopWorkspacesApi = (): DesktopWorkspacesApi | null =>
  (typeof window !== 'undefined' && window.desktopAPI?.workspaces) || null;

/**
 * Clears the desktop shell's stored session and returns its window to the
 * native login screen. No-op (resolves immediately) in the web build or on
 * desktop shells that predate the bridge — callers should still gate on
 * `isDesktopApp()` so a "Sign out" affordance doesn't render where it can't
 * do anything.
 */
export const desktopLogout = (): Promise<void> =>
  (typeof window !== 'undefined' && window.desktopAPI?.auth?.logout()) || Promise.resolve();

/**
 * Pops a native OS notification via the desktop shell (macOS/Windows/Linux).
 * `deepLinkPath` is an in-app router path (e.g. `/notifications`) the shell
 * navigates to — focusing its own window — when the user clicks the toast, so
 * desktop notifications open inside the app rather than the browser. No-op on
 * the web build or an older shell that predates the bridge.
 */
export const showDesktopNotification = (
  title: string,
  body: string,
  deepLinkPath?: string
): void => {
  window.desktopAPI?.app?.showNotification?.(title, body, deepLinkPath);
};

/**
 * Opens the OS notification-settings pane so the user can allow notifications.
 * macOS (and Windows) forbid an app from enabling its own notification
 * permission or re-prompting once denied — deep-linking here is the standard
 * fallback every desktop app uses. No-op on web / older shells.
 */
export const openDesktopNotificationSettings = (): void => {
  window.desktopAPI?.app?.openNotificationSettings?.();
};

// Per-device on/off for desktop native notifications. The OS owns the actual
// permission (and macOS prompts on the first toast), but it exposes no runtime
// "denied" state we can read back, so we keep a local intent flag purely to give
// the settings toggle an "off" the OS can't provide from code. Defaults to on.
const DESKTOP_NOTIF_KEY = 'opal.desktopNotificationsEnabled';

export const desktopNotificationsEnabled = (): boolean => {
  try {
    return localStorage.getItem(DESKTOP_NOTIF_KEY) !== 'false';
  } catch {
    return true;
  }
};

export const setDesktopNotificationsEnabled = (enabled: boolean): void => {
  try {
    localStorage.setItem(DESKTOP_NOTIF_KEY, String(enabled));
  } catch {
    // localStorage unavailable — the toggle just won't persist across reloads.
  }
};
